import React, { FC, useState, useEffect, useRef } from "react";
import { AddBox, Clear, Check, Edit, Delete} from "@mui/icons-material";
import { StyledSectionTable } from './TableInlineEdit.styles';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Input, Button, IconButton, Stack, Autocomplete, Tooltip, Avatar, Grid, TextField } from '@mui/material';
import { Utils } from "../../utils/common-utils";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import { fetchAccountsByName } from "../../store/accountThunk";
import { fetchAccountSkuByName } from "../../store/inventoryStore/inventoryThunk"
import CustomTextField from "../FormFields/CustomTextField";
import LoadingButton from "@mui/lab/LoadingButton/LoadingButton";

interface TableInlineEditProps {
  onChangeRow: (data: any) => void;
  rowsData?: any[];
}

const createData = (id:number, name: string, description:string, spec:string, hsnCode: string, unit:string, qty:number, appliedRate:number, discount:number, amount:number, igst:number, igsttotal:number, cgst:number, cgsttotal:number, sgst:number, sgsttotal:number, total:number, _id:string, skuCode: string) => (
  {
    id,
    name,
    description,
    spec,
    hsnCode,
    unit,
    qty,
    appliedRate,
    discount,
    amount,
    igst,
    igsttotal,
    cgst,
    cgsttotal,
    sgst,
    sgsttotal,
    total,
    isEditMode: false,
    _id, 
    skuCode
  }
);

interface tableCellProps {
  row: any,
  name: string,
  onChange: any,
  align: string,
  type: string,
  isGst?: boolean,
  formatNumber?: boolean,
  readonly?: boolean,
}

const CustomTableCell = ({row, name, onChange, align, type, isGst, formatNumber, readonly}: tableCellProps) => {
  const { isEditMode } = row;
  return (
    <TableCell style={(isGst)? {whiteSpace:'pre-line'} : {}} align={(align==='center')? 'center':'left'} className={'tableCell'}>
      {isEditMode ? (
        <Input
          value={row[name]}
          name={name}
          type={type}
          onChange={e => onChange(e, row, type)}
          className={'inputCell'}
          readOnly={(readonly)? readonly : false}
          error={(type==='text' && row[name] === "")}
        />
      ) : (
        (isGst) ? ((Utils.formatNumber(row[name+'total'], true) || 0)+'\n @ '+(row[name] || 0)+'%') : (formatNumber) ? Utils.formatNumber(row[name], true) : row[name]
      )}
    </TableCell>
  );
};

// TableInlineEdit component. 
const TableInlineEdit: FC<TableInlineEditProps> = ({ onChangeRow, rowsData }) => {

  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [rows, setRows] = useState<any[]>([
    // createData(1, "name", "desc", "spec", 0, 0, 0, 0, 0, 0, 0)
  ]);
  const [previous, setPrevious] = useState<any>({});
  const [isRowAdd, setIsRowAdd] = React.useState(false);

  // autocomplete states
  const elmRef = useRef<any>(null);
  const [options, setOptions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>();
  const [newOption, setNewOption] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const onToggleEditMode = (id:number) => {
    setRows(() => {
      return rows.map(row => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode };
        } 
        return row;
      });
    });
    onChangeRow(rows);
  };

  const onChange = (e:any, row:any, type: string) => {
    if (!previous[row.id]) {
      setPrevious((state: any) => ({ ...state, [row.id]: row }));
    }
    const value = (type!=='number')? e.target.value : (type==='number' && e.target.value>0)? e.target.value : 0;
    const name = e.target.name;
    const { id } = row;
    const newRows = rows.map(row => {
      if (row.id === id) {
        let _qty = (name==='qty')? value : row.qty;
        let _appliedRate = (name==='appliedRate')? value : row.appliedRate;
        let _discount = (name==='discount')? value : row.discount;
        console.log("_discont === " + _discount);
        let isDiscounted = _discount != 0 ? _discount : 0;
        let _igstValue = (name==='igst')? value : row.igst;
        let _cgstValue = (name==='cgst')? value : row.cgst;
        let _sgstValue = (name==='sgst')? value : row.sgst;
        console.log("isDiscounted ===> " + isDiscounted);
        let amount = (_qty* _appliedRate) - isDiscounted;
        let igsttotal = (amount*_igstValue)/100;
        let cgsttotal = (amount*_cgstValue)/100;
        let sgsttotal = (amount*_sgstValue)/100;
        row.amount = Number(amount);
        row.igsttotal = Number(igsttotal);
        row.cgsttotal = Number(cgsttotal);
        row.sgsttotal = Number(sgsttotal);
        row.total = Number(amount+igsttotal+cgsttotal+sgsttotal);

        return { ...row, [name]: value };
      }
      return row;
    });
    setRows(newRows);
  };

  const onRevert = (id: number) => {
    const newRows = rows.map(row => {
      if (row.id === id) {
        return previous[id] ? previous[id] : row;
      }
      return row;
    });
    setRows(newRows);
    setPrevious((state: any[]) => {
      delete state[id];
      return state;
    });
    onToggleEditMode(id);
  };

  const onAdd = () => {
    let rowCount = rows.length+1;
    if (selected) {
      let _qty = 1;
      let _appliedRate = Number(selected.salesPrice)
      let _igstValue = 0;
      let _cgstValue = Number(selected.gst.cgst)
      let _sgstValue = Number(selected.gst.sgst)
      let amount = (_qty* _appliedRate)
      let igsttotal = (amount*_igstValue)/100;
      let cgsttotal = (amount*_cgstValue)/100;
      let sgsttotal = (amount*_sgstValue)/100;
      let total = Number(amount+igsttotal+cgsttotal+sgsttotal);

      setRows([...rows, createData(rowCount, selected.name, selected.description, "", selected.hsnCode, selected.salesUnit, 1, selected.salesPrice, 0, amount, 0, 0, selected.gst.cgst, cgsttotal, selected.gst.sgst, sgsttotal, total, "", selected._id)])
    } else {
      setRows([...rows, createData(rowCount, "", "", "", "", "", 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, "", "")])
    }
    setIsRowAdd(true);
    setSelected('');
  };

  const onDelete = (id: number) => {
    const newRows = rows.filter((obj) => obj.id !== id);
    setRows(newRows);
    onChangeRow(newRows);
  };

  useEffect(() => {
    // console.log("useeffect called");
    // console.log(rows)
    if(isRowAdd) {
      // console.log("useeffect called from add");
      // console.log(rows)
      setIsRowAdd(false);
      let rowCount = rows.length;
      onToggleEditMode(rowCount)
    }
  }, [rows, isRowAdd, onToggleEditMode]);

  useEffect(() => {
    let rowCount = 0;
    setRows([]);
    // if(rows.length==0 && rowsData) {
    if(rowsData && rowsData.length>0) {
      rowsData.forEach((row) => {
        ++rowCount;
        let _row = createData(rowCount, row.name, row.description, row.spec, row.hsnCode, row.unit, row.qty, row.appliedRate, row.discount, row.amount, row.igst, row.igsttotal, row.cgst, row.cgsttotal, row.sgst, row.sgsttotal, row.total, row._id, row.skuCode)
        setRows((rows: any) => ([...rows, _row]));
      });
    }
  }, [rowsData])


  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    setSelected('');
    setNewOption('');
    await dispatch(fetchAccountSkuByName(value)).unwrap().then((data) => {
      if (data) {
        const d = data.inventories.filter(function (item) {
          return item._id !== selectedAccount._id;
        });

        setOptions(d);
      }
    });
  };

  const onChangeHandler = (event: any, value: any) => {
    setNewOption('');
    setIsLoading(true);
    setSelected(value);
  }

  const handleRenderOption = (e: any, b: any, c: any) => {
    console.log('b.name: ', b.name);
    return <div {...e} className='auto-item' key={Math.random()}>
      <div className='img-wrapper'>
        <Avatar
          alt={b.name}
          imgProps={{ referrerPolicy: "no-referrer" }}
          src={b.logo}
          variant='square'
          style={{
            width: "30px",
            height: "30px",
            objectFit: "contain",
          }}
          sizes={"xs"}
        >{b.name.toString().substr(0, 1).toUpperCase()}</Avatar>
      </div>
      <div><b style={{ 'marginRight': '4px' }}>
        {b.name}
      </b>
      </div>
    </div>
  };


  return (
    <>
    <Stack spacing={4} style={{clear: "both",padding:'10px', marginTop: "30px", backgroundColor: '#eff8fe'}}>
      <Stack direction={'row'} spacing={4} style={{justifyContent: 'flex-end'}} >
        <Stack direction={'row'} spacing={2} >
        <Autocomplete
          ref={elmRef}
          id={`Search Inventory`}
          freeSolo
          onChange={onChangeHandler}
          onInputChange={onInputChangeHandler}
          options={options}
          renderOption={handleRenderOption}
          getOptionLabel={option => option.name}
          size="small"
          renderInput={(params) => <TextField {...params} name="search" label={`Search Inventory Line Items`} size='small' style={{width:'400px'}}/>}
        />
        <Button variant='contained' style={{ borderRadius: "30px" }} onClick={onAdd}>Add To Line Item </Button>
        </Stack>
      </Stack>
      
    </Stack>
    <div style={{ display: "flex", flexDirection: 'column', justifyContent: 'flex-end'}}>
    </div>
    <StyledSectionTable>
      <Table className={'table'}>
          <TableHead>
            <TableRow>
              <TableCell title="Name" style={{width: '15%'}} align="left">ITEM</TableCell>
              <TableCell title="Description" style={{width: '15%'}} align="left">Description</TableCell>
              {/* <TableCell title="Specification" style={{width: '12%'}} align="left">Specification</TableCell> */}
              <TableCell title="HSN/SAC Code" style={{width: '7%'}} align="left">HSN/SAC</TableCell>
              <TableCell style={{width: '5%'}} align="center">Unit</TableCell>
              <TableCell title="Qty" style={{width: '5%'}} align="center">Qty</TableCell>
              <TableCell title="MRP" style={{width: '9%'}} align="center">MRP</TableCell>
              <TableCell title="Discounted Rate" style={{width: '9%'}} align="center">DIS (Rs)</TableCell>
              <TableCell title="Amount" style={{width: '9%'}} align="center">Amount</TableCell>
              {/* <TableCell style={{width: '5%'}} align="center">Unit</TableCell> */}
              <TableCell title="IGST%" style={{width: '7%'}} align="center">IGST%</TableCell>
              <TableCell title="CGST%" style={{width: '7%'}} align="center">CGST%</TableCell>
              <TableCell title="SGST%" style={{width: '7%'}} align="center">SGST%</TableCell>
              {/* <TableCell style={{width: '7%'}} align="left">Taxable Value %</TableCell>
              <TableCell style={{width: '7%'}} align="left">Tax (Total)</TableCell> */}
              <TableCell title="Total (Rs.)" style={{width: '7%'}} align="center">Total</TableCell>
              <TableCell title="Actions" style={{width: '7%'}} align="left">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id}>
                <CustomTableCell {...{ row, name: "name", onChange, align: "left", type:"text" }} />
                <CustomTableCell {...{ row, name: "description", onChange, align: "left", type:"text" }} />
                {/* <CustomTableCell {...{ row, name: "spec", onChange, align: "left", type:"text" }} /> */}
                <CustomTableCell {...{ row, name: "hsnCode", onChange, align: "left", type:"text" }} />
                <CustomTableCell {...{ row, name: "unit", onChange, align: "left", type:"text" }} />
                <CustomTableCell {...{ row, name: "qty", onChange, align: "center", type:"number" }} />
                <CustomTableCell {...{ row, name: "appliedRate", onChange, align: "center", type:"number", formatNumber: true }} />
                <CustomTableCell {...{ row, name: "discount", onChange, align: "center", type:"number", formatNumber: true }} />
                <CustomTableCell {...{ row, name: "amount", onChange, align: "center", type:"number", formatNumber: true, readonly: true }} />
              
                <CustomTableCell  {...{ row, name: "igst", onChange, align: "center", type:"number", isGst: true }} />
                <CustomTableCell {...{ row, name: "cgst", onChange, align: "center", type:"number", isGst: true }} />
                <CustomTableCell {...{ row, name: "sgst", onChange, align: "center", type:"number", isGst: true }} />
                {/* <CustomTableCell {...{ row, name: "tax", onChange, align: "center", type:"number", readonly: true }} /> */}
                <CustomTableCell {...{ row, name: "total", onChange, align: "center", type:"number", formatNumber: true, readonly: true }} />
                <TableCell className={'selectTableCell'}>
                  {row.isEditMode ? (
                    <>
                      <IconButton
                        aria-label="done"
                        onClick={() => { if(row.name && row.description) onToggleEditMode(row.id) }}
                      >
                        <Check color="success" />
                      </IconButton>
                      <IconButton
                        aria-label="revert"
                        onClick={() => {
                          if(row.name && row.description) 
                            onRevert(row.id); 
                          else 
                            onDelete(row.id); 
                        }}
                      >
                        <Clear color="error" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        aria-label="Edit"
                        onClick={() => onToggleEditMode(row.id)}
                      >
                        <Edit color="success"/>
                      </IconButton>
                      <IconButton
                        aria-label="Delete"
                        onClick={() => onDelete(row.id)}
                      >
                        <Delete color="error"/>
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
      </Table>
    </StyledSectionTable>
    <Box>
      <Grid container style={{ justifyContent: 'flex-start', padding: '20px', backgroundColor: '' }}>
        {/* add form fields */}
        <CustomTextField className={'hiddenText'} variant="standard" label="" name="lineItem" required />
        <Button style={{ marginLeft: '16px', marginBottom: '0px' }} className={'addButtonRoot'}  onClick={onAdd}>
        Create Line Item <AddBox />
      </Button>
      </Grid>
    </Box>
      </>
  );
}

export default TableInlineEdit;